<script setup lang="ts">
/* =============================================================
   BOOKINGS — LIST
   =============================================================
   Every booking on the platform, newest arrival first, with a
   status filter that defaults to everything. "Awaiting owner"
   is the one worth a glance: those are manual-confirm requests
   holding a slot until someone answers.

   No "New booking" button. request_booking acts for the
   caller's own company and staff have none -- bookings come from
   the app, or from the demo seed.
   ============================================================= */

import { computed, onMounted, ref } from 'vue'
import PageHeader from '@/components/ui/PageHeader.vue'
import Panel from '@/components/ui/Panel.vue'
import Button from '@/components/ui/Button.vue'
import Alert from '@/components/ui/Alert.vue'
import EmptyState from '@/components/ui/EmptyState.vue'
import FilterInput from '@/components/ui/FilterInput.vue'
import SelectField from '@/components/ui/SelectField.vue'
import BookingList from '@/components/BookingList.vue'
import { listBookings } from '@/lib/bookings'
import { BOOKING_STATES, bookingState, bookingStateLabel } from '@/lib/bookingLabels'
import { errorMessage } from '@/lib/errors'
import type { Booking } from '@/lib/types'

const rows = ref<Booking[]>([])
const loading = ref(true)
const loadError = ref('')
const filter = ref('')
const state = ref('')

const visible = computed(() => {
  const q = filter.value.trim().toLowerCase()
  return rows.value.filter((b) => {
    if (state.value && bookingState(b) !== state.value) return false
    if (!q) return true
    return (
      b.reference.toLowerCase().includes(q) ||
      (b.berth?.name ?? '').toLowerCase().includes(q) ||
      (b.berth?.city ?? '').toLowerCase().includes(q) ||
      (b.vessel?.name ?? '').toLowerCase().includes(q) ||
      (b.company?.name ?? '').toLowerCase().includes(q)
    )
  })
})

const countLabel = computed(() => {
  const total = rows.value.length
  const shown = visible.value.length
  const noun = total === 1 ? 'booking' : 'bookings'
  return shown === total ? `${total} ${noun}` : `${shown} of ${total} ${noun}`
})

async function load(): Promise<void> {
  loading.value = true
  loadError.value = ''
  try {
    rows.value = await listBookings()
  } catch (e) {
    loadError.value = errorMessage(e, 'Could not load bookings.')
  } finally {
    loading.value = false
  }
}

onMounted(load)
</script>

<template>
  <PageHeader title="Bookings" description="Every booking on the platform, newest arrival first.">
    <template #actions>
      <Button variant="ghost" icon="refresh" :loading="loading" @click="load">Refresh</Button>
    </template>
  </PageHeader>

  <Alert v-if="loadError" tone="danger" class="page-alert">{{ loadError }}</Alert>

  <Panel flush :title="rows.length > 0 ? countLabel : undefined">
    <template v-if="rows.length > 0" #actions>
      <SelectField v-model="state" label="Status" class="stateFilter">
        <option value="">All statuses</option>
        <option v-for="s in BOOKING_STATES" :key="s" :value="s">{{ bookingStateLabel(s) }}</option>
      </SelectField>
      <FilterInput
        v-model="filter"
        label="Filter bookings by reference, berth, vessel or company"
        placeholder="Filter by reference, berth, vessel or company"
      />
    </template>

    <p v-if="loading && rows.length === 0" class="panel-status">Loading…</p>

    <EmptyState
      v-else-if="rows.length === 0"
      icon="bookings"
      title="No bookings yet"
      description="Bookings are placed from the app by a ship operator, or loaded with the demo data."
    />

    <p v-else-if="visible.length === 0" class="panel-status">No booking matches the filter.</p>

    <BookingList v-else :bookings="visible" />
  </Panel>
</template>

<style scoped>
/* The label is for screen readers; the header row is too tight for it. */
.stateFilter :deep(label) {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
}

.stateFilter {
  min-width: 10rem;
}

@media (max-width: 40rem) {
  .stateFilter {
    width: 100%;
  }
}
</style>
